import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SquareKanban, Building2, Users, Instagram, ListChecks, ShieldQuestion, Upload, TrendingUp, ShieldCheck, Sparkles, ChevronsUpDown, ChevronDown, FlaskConical, Flag, Clapperboard, Download, ClipboardCheck } from 'lucide-react';

import { ROUTES, groupNavSections, isSectionActive } from '../../../shared/config/routes';
import { DIVISION_ROLE_LABEL, usePipelineContext, useVisibleNavSections } from '../../../entities/pipeline';
import { isTestEnvironment } from '../../../shared/config/environment';
import { useCurrentUser } from '../../../shared/hooks/useCurrentUser';
import { ChangelogDialog, useChangelogSeen } from '../../../features/changelog';
import { ProfileDialog } from '../../profile-dialog';
import { PushToggle } from '../../../features/montage/components/PushToggle';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { initialsOf, loginOf, useSignedInEmail } from '../../../shared/hooks/useSignedInEmail';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
} from '../../../components/ui/sidebar';

/** Иконки держим рядом с разделами, а сами разделы — в routes.ts, общие с мобильным таб-баром. */
const SECTION_ICONS = {
  // Флажок — тот же знак, что стоит у темы в списке на самом экране.
  [ROUTES.MONTAGE_TOPICS]: Flag,
  [ROUTES.MONTAGE]: SquareKanban,
  [ROUTES.MY_TASKS]: ListChecks,
  [ROUTES.VIDEOS]: Clapperboard,
  [ROUTES.TAKES]: Download,
  [ROUTES.WORK]: ClipboardCheck,
  [ROUTES.MODERATION]: ShieldQuestion,
  [ROUTES.DIVISION]: Building2,
  [ROUTES.STATS]: TrendingUp,
  [ROUTES.PUBLICATIONS]: Upload,
  [ROUTES.ACCOUNTS]: Users,
} as Record<string, typeof Users>;

export function AppSidebar() {
  const location = useLocation();
  const { isAdmin } = useCurrentUser();
  const sections = useVisibleNavSections() ?? [];
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [videosOpen, setVideosOpen] = useState(location.pathname.startsWith(ROUTES.VIDEOS));
  const email = useSignedInEmail();
  const { hasUnread, markSeen } = useChangelogSeen();
  useEffect(() => {
    if (location.pathname.startsWith(ROUTES.VIDEOS)) setVideosOpen(true);
  }, [location.pathname]);

  // Имя и роль берём из того же ответа, по которому считается меню, — он уже
  // в кэше, второго запроса не будет. Логин из почты остаётся запасным
  // вариантом: у людей вне команд имени в конвейере нет, и «timur» лучше
  // пустоты. Подпись «Сотрудник» тоже: она честна ровно для них.
  const { context, primaryDivision, projects } = usePipelineContext();
  const fullName = context?.user.fullName ?? null;
  const avatarUrl = context?.user.avatarUrl ?? null;
  const displayName = fullName ?? (email ? loginOf(email) : 'Профиль');
  const roleLine = isAdmin
    ? 'Администратор'
    : primaryDivision
      ? `${DIVISION_ROLE_LABEL[primaryDivision.role]} · ${primaryDivision.name}`
      : 'Сотрудник';

  return (
    <Sidebar>
      <SidebarHeader>
        <Link to={ROUTES.HOME} className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-sidebar-accent">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary">
            <Instagram className="h-5 w-5 text-primary-foreground" />
          </div>
          <div className="flex flex-col gap-0.5 leading-none">
            <span className="font-semibold">InstaFarm</span>
            <span className="text-xs text-muted-foreground">Galamat Media</span>
          </div>
        </Link>
      </SidebarHeader>

      <SidebarContent>
        {groupNavSections(sections).map((group) => (
          <SidebarGroup key={group.caption ?? 'единственный'}>
            {group.caption && <SidebarGroupLabel>{group.caption}</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((section) => {
                  const Icon = SECTION_ICONS[section.path] ?? Users;
                  const isActive = isSectionActive(location.pathname, section.match, (section as { end?: boolean }).end);

                  if (section.path === ROUTES.VIDEOS) return (
                    <SidebarMenuItem key={section.path}>
                      <SidebarMenuButton asChild isActive={isActive} tooltip={section.label}>
                        <Link to={section.path}>
                          <Icon />
                          <span>{section.label}</span>
                        </Link>
                      </SidebarMenuButton>
                      <SidebarMenuAction onClick={() => setVideosOpen((open) => !open)} aria-label={videosOpen ? 'Свернуть статистику видео' : 'Раскрыть статистику видео'}>
                        <ChevronDown className={`transition-transform ${videosOpen ? 'rotate-180' : ''}`} />
                      </SidebarMenuAction>
                      {videosOpen && (
                        <SidebarMenuSub>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton asChild isActive={isActive && !new URLSearchParams(location.search).get('direction')}>
                              <Link to={ROUTES.VIDEOS}><span>Все проекты</span></Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          {projects.map((project) => (
                            <SidebarMenuSubItem key={project.id}>
                              <SidebarMenuSubButton asChild isActive={isActive && new URLSearchParams(location.search).get('direction') === project.id}>
                                <Link to={`${ROUTES.VIDEOS}?direction=${encodeURIComponent(project.id)}`}><span>{project.name}</span></Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      )}
                    </SidebarMenuItem>
                  );

                  return (
                    <SidebarMenuItem key={section.path}>
                      <SidebarMenuButton asChild isActive={isActive} tooltip={section.label}>
                        <Link to={section.path}>
                          <Icon />
                          <span>{section.label}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip="Что нового"
              onClick={() => {
                setChangelogOpen(true);
                markSeen();
              }}
            >
              <Sparkles />
              <span>Что нового</span>
              {hasUnread && (
                <span
                  aria-label="есть непрочитанное"
                  className="ml-auto size-1.5 shrink-0 rounded-full bg-primary"
                />
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
          <PushToggle>
            {({ icon: Icon, on, busy, onClick }) => (
              <SidebarMenuItem>
                <SidebarMenuButton tooltip={on ? 'Уведомления включены' : 'Включить уведомления'} onClick={onClick} disabled={busy} isActive={false}>
                  <Icon className={on ? 'text-primary' : undefined} />
                  <span>{on ? 'Уведомления включены' : 'Включить уведомления'}</span>
                  {!on && <span aria-hidden className="ml-auto size-1.5 shrink-0 rounded-full bg-destructive" />}
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
          </PushToggle>
          {isAdmin && (
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={location.pathname.startsWith(ROUTES.TEAM)}
                tooltip="Сотрудники и доступы"
              >
                <Link to={ROUTES.TEAM}>
                  <ShieldCheck />
                  <span>Сотрудники и доступы</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          {/* Витрина компонентов существует только на тестовом стенде — на бою
              и ссылка, и страница ни к чему. */}
          {isTestEnvironment() && (
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={location.pathname.startsWith(ROUTES.UI_KIT)}
                tooltip="Витрина компонентов"
              >
                <Link to={ROUTES.UI_KIT}>
                  <FlaskConical />
                  <span>Компоненты</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
        </SidebarMenu>

        <SidebarSeparator className="mx-0" />

        <SidebarMenu>
          <SidebarMenuItem>
            {/* Аккаунт — последним и крупнее остального: это не пункт меню, а
                «кто сейчас в системе». Профиль открывается окном, уводить со
                страницы ради «кто я и за что отвечаю» незачем. */}
            <SidebarMenuButton
              size="lg"
              onClick={() => setProfileOpen(true)}
              isActive={profileOpen}
              tooltip="Профиль"
            >
              <Avatar className="size-8 rounded-lg">
                <AvatarImage src={avatarUrl ?? undefined} alt="" className="rounded-lg" />
                <AvatarFallback className="rounded-lg text-xs font-semibold">
                  {initialsOf(displayName)}
                </AvatarFallback>
              </Avatar>
              <span className="grid flex-1 text-left leading-tight">
                <span className="truncate font-medium">{displayName}</span>
                <span className="truncate text-xs text-muted-foreground">{roleLine}</span>
              </span>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <ChangelogDialog open={changelogOpen} onOpenChange={setChangelogOpen} />
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
    </Sidebar>
  );
}
